import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, ShieldCheck, ShieldAlert, WifiOff, RefreshCw, X, KeyRound } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { CopyBtn, prettyBody } from "@/components/payment/VendorResponse";

const VERDICTS = {
    reachable: { icon: ShieldCheck, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", title: "Connected — vendor accepted this server's IP",
                 hint: "Payspher answered the probe. Any business error below (e.g. payment not found) is expected for a test request." },
    ip_blocked: { icon: ShieldAlert, cls: "bg-rose-50 border-rose-200 text-rose-800", title: "Blocked — IP not whitelisted by vendor",
                  hint: "Send the report below to Payspher. Ask them to whitelist the outbound IP for this exact endpoint and auth key (production vs UAT)." },
    unreachable: { icon: WifiOff, cls: "bg-amber-50 border-amber-200 text-amber-800", title: "Unreachable — could not reach the endpoint",
                   hint: "Check the API base URL, DNS and outbound firewall / security group rules on the server." },
    misconfigured: { icon: KeyRound, cls: "bg-orange-50 border-orange-200 text-orange-800", title: "Misconfigured — vendor rejected the credentials",
                     hint: "The IP is accepted but the auth key or secret key is wrong for this endpoint. Edit the MID and re-enter both keys." },
};

const Row = ({ k, v, copy, testid }) => (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
        <span className="text-[11px] uppercase tracking-wider text-slate-400 shrink-0">{k}</span>
        <span className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-mono text-slate-800 truncate" data-testid={testid} title={String(v ?? "")}>{v ?? "—"}</span>
            {copy && v && <CopyBtn text={String(v)} label="" />}
        </span>
    </div>
);

export function PayinConnectionTest({ mid, onClose }) {
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const run = async () => {
        setBusy(true); setErr("");
        try { const r = await api.post(`/admin/payin/mids/${mid.mid_id}/test-connection`); setRes(r.data); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const v = res && VERDICTS[res.verdict];
    const report = res && [
        `Connection test — ${mid.label} (${mid.vendor})`,
        `Tested at: ${res.tested_at}`,
        `Outbound server IP: ${res.egress_ip}`,
        `Endpoint: ${res.endpoint}`,
        `Auth key: ${res.auth_key_masked}`,
        `Request mode: ${res.request_mode}`,
        `HTTP status: ${res.http_status ?? "network error"}`,
        "Vendor response:", prettyBody(res.response),
    ].join("\n");

    return createPortal(
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-xl w-full max-w-xl p-5 space-y-4 shadow-xl" data-testid="payin-conn-test-modal">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-bold text-base">Test connection</h3>
                        <p className="text-[11px] text-slate-500 mt-0.5">{mid.label} · <span className="font-mono">{mid.mid_id}</span></p>
                    </div>
                    <button onClick={onClose} data-testid="payin-conn-test-close" className="p-1 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
                </div>

                {busy && !res && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center" data-testid="payin-conn-test-loading">
                        <Loader2 className="w-4 h-4 animate-spin" /> Probing vendor…
                    </div>
                )}
                {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700" data-testid="payin-conn-test-error">{err}</div>}

                {res && v && (
                    <>
                        <div className={`flex gap-3 border rounded-lg p-3 ${v.cls}`} data-testid={`payin-conn-verdict-${res.verdict}`}>
                            <v.icon className="w-5 h-5 shrink-0 mt-0.5" />
                            <div>
                                <div className="text-sm font-semibold">{v.title}</div>
                                <div className="text-[11px] mt-0.5 opacity-90">{v.hint}</div>
                            </div>
                        </div>
                        <div className="border border-slate-200 rounded-lg px-3">
                            <Row k="Outbound IP" v={res.egress_ip} copy testid="payin-conn-egress-ip" />
                            <Row k="Endpoint" v={res.endpoint} copy testid="payin-conn-endpoint" />
                            <Row k="Auth key" v={res.auth_key_masked} testid="payin-conn-auth" />
                            <Row k="HTTP status" v={`${res.http_status ?? "network error"} · ${res.latency_ms} ms`} testid="payin-conn-http" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Raw vendor response</div>
                            <pre data-testid="payin-conn-raw" className="text-[11px] leading-relaxed font-mono bg-slate-50 border border-slate-100 rounded p-2.5 max-h-48 overflow-auto whitespace-pre-wrap break-all text-slate-700">{prettyBody(res.response)}</pre>
                        </div>
                    </>
                )}

                <div className="flex items-center justify-between pt-1">
                    {report ? <CopyBtn text={report} label="Copy report for vendor" testid="payin-conn-copy-report" /> : <span />}
                    <button onClick={run} disabled={busy} data-testid="payin-conn-retest"
                            className="px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-test
                    </button>
                </div>
            </div>
        </div>, document.body);
}
